import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Users, CalendarDays, FileText, Receipt, ArrowUpRight, TrendingUp, ShieldCheck, Wallet } from 'lucide-react';
import ResumeDraftsCard from '../../components/ResumeDraftsCard';
import SubscriptionWidget from '../../components/SubscriptionWidget';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [finance, setFinance] = useState(null);
  const [pending, setPending] = useState([]);

  useEffect(() => {
    api.get('/dashboard/admin').then(r => setData(r.data)).catch(() => {});
    // Admin-only endpoints — skip for clinicians so we don't trigger 403s
    if (user?.role === 'admin') {
      api.get('/finance/summary').then(r => setFinance(r.data)).catch(() => {});
    }
    const loadPending = () => api.get('/payments/pending').then(r => setPending(r.data)).catch(() => {});
    loadPending();
    // Poll every 45s so new 'I've paid' confirmations surface quickly
    const t = setInterval(loadPending, 45_000);
    return () => clearInterval(t);
  }, [user?.role]);

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CLINIC OVERVIEW</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Welcome, {user?.name}</h1>
          <p className="text-fg-muted mt-2">Today's pulse of your physiotherapy clinic.</p>
        </div>
        {pending.length > 0 && (
          <Link
            to="/admin/appointments"
            className="relative inline-flex items-start gap-3 rounded-xl border px-4 py-3 transition hover:shadow-sm"
            style={{ borderColor: '#FFB00055', background: 'linear-gradient(135deg, #FFF4E0, #FFE0B3)' }}
            data-testid="pending-verifications-card"
          >
            <span className="absolute top-2 right-2 h-2 w-2 rounded-full animate-pulse" style={{ background: '#FFB000' }}/>
            <Wallet className="w-5 h-5 mt-0.5" style={{ color: '#FFB000' }}/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#A86F31' }}>
                Payment verifications
              </div>
              <div className="font-heading font-bold text-2xl leading-tight text-[#2A2D2A]" data-testid="pending-count">
                {pending.filter(p => p.prepay_status === 'pending_verification').length}
                <span className="text-sm text-fg-muted font-normal"> awaiting verify</span>
              </div>
              <div className="text-[11px] text-fg-muted mt-0.5 inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3"/>
                {pending.filter(p => p.prepay_status === 'awaiting_payment').length} still to pay · Tap to review →
              </div>
            </div>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Users} label="Patients" value={data?.patients ?? '—'} testid="stat-patients" />
        {/* Iter 72 — clinicians don't see Appointments / Invoices stats (user request).
            They focus on Patients + Documents only. */}
        {user?.role === 'admin' && (
          <Stat icon={CalendarDays} label="Appointments" value={data?.appointments ?? '—'} testid="stat-appointments" />
        )}
        <Stat icon={FileText} label="Documents" value={data?.documents ?? '—'} testid="stat-documents" />
        {user?.role === 'admin' && (
          <Stat icon={Receipt} label="Invoices" value={data?.invoices ?? '—'} testid="stat-invoices" />
        )}
      </div>

      {/* Iter 145 — friendly getting-started empty state when the clinic is brand new. */}
      {data && !data.patients && !data.appointments && !data.documents && !data.invoices && (
        <div className="admin-card mb-8 border-dashed" data-testid="dashboard-empty-state">
          <h3 className="font-heading font-semibold mb-1">Your clinic is ready — let's add the first data</h3>
          <p className="text-sm text-fg-muted mb-3">No patients, appointments, documents or invoices yet. Start with any of these:</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/patients" className="btn-primary text-xs" data-testid="empty-add-patient">Add your first patient</Link>
            <Link to="/admin/services" className="btn-secondary text-xs" data-testid="empty-setup-services">Set up services & pricing</Link>
            <Link to="/admin/invite" className="btn-secondary text-xs" data-testid="empty-invite">Invite patients (QR / link)</Link>
            <Link to="/admin/hr" className="btn-secondary text-xs" data-testid="empty-hire">Hire staff (HR)</Link>
          </div>
        </div>
      )}

      {/* Iter 125 — Live subscription-status widget. Only renders for the
          clinic owner (silently 403s + hides for other admins). */}
      {user?.role === 'admin' && <SubscriptionWidget />}

      {/* Iter 70 — surface in-flight autosave drafts so clinicians can pick up
          where they left off after a phone-sleep / day off. */}
      <ResumeDraftsCard role={user?.role || 'admin'}/>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Iter 72 — Upcoming sessions panel is admin-only now. Clinicians
            focus on their patient list, not the schedule. */}
        {user?.role === 'admin' && (
          <div className="lg:col-span-2 admin-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Upcoming sessions</h3>
              <Link to="/admin/appointments" className="text-xs text-brand inline-flex items-center gap-1" data-testid="link-appointments">
                View all <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            {data?.upcoming?.length ? (
              <div className="divide-y border-soft">
                {data.upcoming.map((a) => (
                  <Link key={a.appointment_id} to={`/admin/patients/${a.patient_id}`}
                        className="py-3 flex items-center justify-between hover:bg-bg-secondary/30 transition px-2 -mx-2 rounded"
                        data-testid={`dash-appt-${a.appointment_id}`}>
                    <div>
                      <div className="font-medium text-sm">{a.patient_name}</div>
                      <div className="text-xs text-fg-muted">{new Date(a.starts_at).toLocaleString()}</div>
                    </div>
                    <span className="badge-neutral">{a.duration_min} min</span>
                  </Link>
                ))}
              </div>
            ) : <div className="text-sm text-fg-muted py-6">No upcoming sessions.</div>}
          </div>
        )}

        {/* Iter 68 — Revenue card is admin-only. Iter 72 — weekly calendar removed for clinicians (user request). */}
        {user?.role === 'admin' && (
          <div className="admin-card">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-brand" /> Revenue
            </h3>
            <div className="text-3xl font-heading font-bold">
              {finance ? `${finance.total_revenue.toFixed(2)}` : '—'}
            </div>
            <div className="text-xs text-fg-muted mt-1">paid invoices total</div>
            <div className="mt-4 space-y-2">
              <Row label="Outstanding" value={finance ? `${finance.total_outstanding.toFixed(2)}` : '—'} />
              <Row label="Completed" value={finance?.appointments_completed ?? '—'} />
              <Row label="Cancelled" value={finance?.appointments_cancelled ?? '—'} />
            </div>
          </div>
        )}

        {/* Recent invoices — admin only (clinicians don't see money). */}
        {user?.role === 'admin' && (
          <div className="admin-card lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Recent invoices</h3>
              <Link to="/admin/finances" className="text-xs text-brand">Open finances</Link>
            </div>
            {data?.recent_invoices?.length ? (
              <table>
                <thead><tr><th>#</th><th>Patient</th><th>Total</th><th>Status</th><th>Issued</th></tr></thead>
                <tbody>
                  {data.recent_invoices.map(i => (
                    <tr key={i.invoice_id}>
                      <td className="font-mono text-xs">{i.invoice_number}</td>
                      <td>{i.patient_name}</td>
                      <td>{i.total.toFixed(2)} {i.currency}</td>
                      <td><StatusBadge s={i.status} /></td>
                      <td className="text-xs text-fg-muted">{i.issued_at?.slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="text-sm text-fg-muted py-6">No invoices yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}


function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="admin-card" data-testid={testid}>
      <Icon className="w-5 h-5 text-brand mb-3" strokeWidth={1.6} />
      <div className="text-3xl font-heading font-bold">{value}</div>
      <div className="text-xs text-fg-muted mt-1">{label}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-fg-muted">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function StatusBadge({ s }) {
  const cls = s === 'paid' ? 'badge-success' : s === 'unpaid' ? 'badge-warn' : 'badge-error';
  return <span className={cls}>{s}</span>;
}
